import React from 'react';
import LearningObjectives from '../../../../../components/LearningObjectives/LearningObjectives';
import { REPORT_TYPES } from '../../../../actions/constants';
import style from '../QuantQual.module.css';

const NULL_STATE_TYPE = {
  noAssignments: 'noAssignments',
  noActivity: 'noActivity',
  noStudents: 'noStudents',
};

const reportNameMapping = {
  [REPORT_TYPES.unit]: 'Unit',
  [REPORT_TYPES.subunit]: 'Subunit',
};

const getNullState = (data, hasStudents, onTabChange, reportType) => {
  const noStartedQuestions = (los) => {
    let sq = false;
    for (let i = 0; i < los.length; i++) {
      sq = los[i].children.every(e => e.startedQuestions < 0);
    }
    return los.every(e => e.startedQuestions < 0) && sq;
  };

  let nullStateType = null;
  if (!hasStudents) {
    nullStateType = NULL_STATE_TYPE.noStudents;
  } else if (!data || !data.length) {
    nullStateType = NULL_STATE_TYPE.noAssignments;
  } else if (noStartedQuestions(data)) {
    nullStateType = NULL_STATE_TYPE.noActivity;
  }

  if (!nullStateType) return null;

  const reportName = reportNameMapping[reportType] || 'Subunit';
  return (
    <div className={style.qqLOTitleContainerNoText}>
      <p className={style.qqLONoDataText}>
        {{
          [NULL_STATE_TYPE.noStudents]: <span>
            No students have enrolled in this course.<br />
            Go to the <button className={style.qqLONoDataLink} role="link" onClick={() => { onTabChange('people'); }}>People</button> tab to invite students to enroll.
          </span>,
          [NULL_STATE_TYPE.noAssignments]: <span>
            There are no assignments with Learning Objectives in this {reportName}.<br />
            Go to <button role="link" className={style.qqLONoDataLink} onClick={() => { onTabChange('assignments'); }}>Course Planner</button> tab to setup and manage assignments.
          </span>,
          [NULL_STATE_TYPE.noActivity]: <span>No students have started assignments in this {reportName}. As they work on assignments with Learning Objectives, you will see their performance updated here.</span>,
        }[nullStateType]}
      </p>
    </div>);
};

const getBeautifyData = data => data.map(row => ({
  ...row,
  isNullState: !(row.correctOnFirstTry >= 0),
  correctOnFirstTrySortValue: row.correctOnFirstTry,
  correctOnFirstTry: row.correctOnFirstTry >= 0 ? `${Math.round(row.correctOnFirstTry * 100)}%` : '—',
  completedQuestions: row.completedQuestions >= 0 ? `${Math.round(row.completedQuestions * 100)}%` : '—',
  children: row.children.map(element => ({
    ...element,
    isNullState: !(element.correctOnFirstTry >= 0),
    correctOnFirstTrySortValue: element.correctOnFirstTry,
    correctOnFirstTry: element.correctOnFirstTry >= 0 ? `${Math.round(element.correctOnFirstTry * 100)}%` : '—',
    completedQuestions: element.completedQuestions >= 0 ? `${Math.round(element.completedQuestions * 100)}%` : '—',
    startedQuestions: element.startedQuestions >= 0 ? element.startedQuestions : '—',
  })),
}));

const getLOElements = (learningObjectiveData, hasStudents, onTabChange, reportType) => ({
  modal: {
    title: (<p>Performance by Learning Objective</p>),
    content: (
      <div>
        <p>
          <b>Average # of questions started per student</b> represents the number of questions assessing this learning objective that a student in your class probably began
            (for some assignments this number is variable by student based on performance which is why it is presented as an average).
              <br />
        </p>
        <p>
          <b>Questions completed</b> represents, across all students in your class, the proportion of started questions assessing this learning objective that your students completed.
              <br />
        </p>
        <p>
          <b>Questions correct on first try</b> represents, across all attempted questions assessing this learning objective, the proportion that were correct on a student&#39;s first try.
              <br />
        </p>
      </div>
      ),
  },
  headers: [
    {
      accessor: 'name',
      Header: 'Objective Title',
    },
    {
      accessor: 'startedQuestions',
      Header: 'Average # of Questions Started Per Student',
    },
    {
      accessor: 'completedQuestions',
      Header: 'Questions Completed',
    },
    {
      accessor: 'correctOnFirstTry',
      sortValue: 'correctOnFirstTrySortValue',
      Header: 'Questions Correct On First Try',
    },
  ],
  cellStyling: [
    {
      accessor: 'startedQuestions',
      alignment: 'right',
    },
    {
      accessor: 'completedQuestions',
      alignment: 'right',
    },
    {
      accessor: 'correctOnFirstTry',
      alignment: 'right',
    },
  ],
  nullStateMsg: getNullState(learningObjectiveData, hasStudents, onTabChange, reportType),
  title: (<div className={style.qqLOTitle}>Performance by Learning Objective</div>),
  subtitleTextFunc: (row, amount) => (
    <div>
        Students have scored below 60% on
        <b>{` ${row.lowScoring} of ${amount} `}</b>
      {`Learning Objective${amount > 1 ? 's' : ''}`}
    </div >
      ),
});

const performanceByLearningObjectiveComponent = (data, hasStudents, onTabChange, reportType) => {
  const lOElements = getLOElements(data, hasStudents, onTabChange, reportType);
  const formattedPerformanceByLearningObjective = getBeautifyData(data);

  return (
    <LearningObjectives
      data={formattedPerformanceByLearningObjective}
      onTabChange={onTabChange}
      reportType={reportType}
      accessors={lOElements.headers}
      modal={lOElements.modal}
      nullStateMessage={lOElements.nullStateMsg}
      getSubtitleLOText={lOElements.subtitleTextFunc}
      primaryMeasureAccessor="correctOnFirstTry"
      cellStyling={lOElements.cellStyling}
    />
  );
};

export default performanceByLearningObjectiveComponent;
